import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Download,
  Pencil,
  ExternalLink,
  Loader2,
  Smartphone,
  Monitor,
  Tablet,
  Bot,
  Globe,
  FileDown,
  Bell,
  Lock,
  Sparkles,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import Navbar from "@/components/Navbar";
import CampaignModal from "@/components/CampaignModal";
import ShareToggle from "@/components/ShareToggle";
import QRStylePanel from "@/components/QRStylePanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/context/BillingContext";

function DeviceIcon({ device }) {
  const cls = "w-3.5 h-3.5";
  if (device === "mobile") return <Smartphone className={cls} />;
  if (device === "tablet") return <Tablet className={cls} />;
  if (device === "desktop") return <Monitor className={cls} />;
  if (device === "bot") return <Bot className={cls} />;
  return <Globe className={cls} />;
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPro } = useBilling();
  const [campaign, setCampaign] = useState(null);
  const [scans, setScans] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [rangeDays, setRangeDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async (days = rangeDays) => {
    try {
      const [{ data: c }, { data: s }, { data: t }] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/scans`),
        api.get(`/campaigns/${id}/timeline?days=${days}`),
      ]);
      setCampaign(c);
      setScans(s);
      setTimeline(
        t.series.map((d) => ({
          ...d,
          label:
            days <= 14
              ? new Date(d.date).toLocaleDateString(undefined, { weekday: "short", day: "numeric" })
              : new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        }))
      );
    } catch (e) {
      toast.error(formatApiError(e));
      if (e.response?.status === 404) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const changeRange = async (days) => {
    setRangeDays(days);
    try {
      const { data: t } = await api.get(`/campaigns/${id}/timeline?days=${days}`);
      setTimeline(
        t.series.map((d) => ({
          ...d,
          label:
            days <= 14
              ? new Date(d.date).toLocaleDateString(undefined, { weekday: "short", day: "numeric" })
              : new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        }))
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async () => {
    if (!campaign) return;
    await navigator.clipboard.writeText(campaign.short_url);
    setCopied(true);
    toast.success("Short link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const downloadQR = () => {
    if (!campaign?.qr_image) return;
    const a = document.createElement("a");
    a.href = campaign.qr_image;
    a.download = `qdurator-${campaign.shortcode}.png`;
    a.click();
    toast.success("QR image downloaded");
  };

  const downloadCsv = async () => {
    if (!isPro) {
      toast.error("CSV export is a Pro feature. Upgrade to unlock.");
      return;
    }
    try {
      const token = localStorage.getItem("qdurator_token");
      const res = await fetch(`${API}/campaigns/${id}/scans.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scans-${campaign.shortcode}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Could not export CSV");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-6 transition-colors"
          data-testid="back-to-dashboard-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600">
              Campaign detail
            </span>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2"
              data-testid="campaign-detail-name"
            >
              {campaign.name}
            </h1>
            <div className="mt-2 flex items-center gap-3 text-sm text-slate-500 flex-wrap">
              <span className="font-mono font-bold text-slate-700">
                /{campaign.shortcode}
              </span>
              <span>·</span>
              <span>
                Created{" "}
                {new Date(campaign.created_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
              {campaign.folder && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200" data-testid="campaign-folder-badge">
                  📁 {campaign.folder}
                </span>
              )}
              {campaign.alert_enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200" data-testid="campaign-alerts-badge">
                  <Bell className="w-3 h-3" />
                  Alerts on ({campaign.alert_threshold}/{campaign.alert_window_hours}h)
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            variant="outline"
            className="h-10 px-4 rounded-xl border-slate-300 font-semibold"
            data-testid="edit-campaign-detail-btn"
          >
            <Pencil className="w-4 h-4 mr-1.5" />
            Edit
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* QR panel */}
          <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">QR code</h2>
              <Badge
                variant="secondary"
                className="bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                LIVE
              </Badge>
            </div>
            <div className="aspect-square rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center p-6">
              <img
                src={campaign.qr_image}
                alt="QR code"
                className="w-full h-full object-contain"
                data-testid="campaign-detail-qr-image"
              />
            </div>
            <Button
              onClick={downloadQR}
              className="w-full mt-4 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              data-testid="download-qr-button"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download PNG
            </Button>
          </div>

          {/* Info panel */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                Short link
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <code
                  className="font-mono text-base sm:text-lg font-semibold text-slate-900 bg-slate-100 px-3 py-1.5 rounded-lg break-all"
                  data-testid="campaign-detail-short-url"
                >
                  {campaign.short_url}
                </code>
                <button
                  onClick={copyLink}
                  data-testid="campaign-detail-copy-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </>
                  )}
                </button>
              </div>

              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  Current destination
                </div>
                <a
                  href={campaign.destination_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium break-all"
                  data-testid="campaign-detail-destination-url"
                >
                  {campaign.destination_url}
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                </a>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Total scans
                  </div>
                  <div
                    className="mt-1 text-2xl font-black font-mono text-slate-900 tabular-nums"
                    data-testid="detail-total-scans"
                  >
                    {(campaign.scan_count || 0).toLocaleString()}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Recorded events
                  </div>
                  <div className="mt-1 text-2xl font-black font-mono text-slate-900 tabular-nums">
                    {scans.length.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share + QR style */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ShareToggle campaign={campaign} onChanged={() => load()} />
          <QRStylePanel campaign={campaign} onChanged={() => load()} />
        </div>

        {/* Timeline */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6" data-testid="campaign-timeline-chart">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Scans over time</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Spot spikes tied to specific print runs, posts, or campaigns.
              </p>
            </div>
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" role="tablist">
              {[7, 14, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => changeRange(d)}
                  data-testid={`timeline-range-${d}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    rangeDays === d
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="detailLineGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#06B6D4" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="scans"
                  stroke="url(#detailLineGrad)"
                  strokeWidth={3}
                  dot={{ r: 3, fill: "#2563EB" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Scan history */}
        <div className="mt-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Scan history</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Every scan is logged with timestamp, device, IP, and location.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={downloadCsv}
              disabled={scans.length === 0}
              className="h-9 rounded-xl border-slate-300 text-sm font-semibold"
              data-testid="export-csv-button"
              title={!isPro ? "Pro feature" : undefined}
            >
              {!isPro && <Lock className="w-3.5 h-3.5 mr-1 text-amber-500" />}
              <FileDown className="w-4 h-4 mr-1.5" />
              Export CSV
              {!isPro && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px] font-black tracking-wider border border-amber-200">
                  PRO
                </span>
              )}
            </Button>
          </div>

          <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full" data-testid="scan-history-table">
              <thead>
                <tr className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-6 py-3 text-left">Timestamp</th>
                  <th className="px-6 py-3 text-left">Device</th>
                  <th className="px-6 py-3 text-left">Location</th>
                </tr>
              </thead>
              <tbody>
                {scans.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-16 text-center">
                      <div className="text-sm text-slate-500">
                        No scans yet. Share your QR code to see events appear here.
                      </div>
                    </td>
                  </tr>
                ) : (
                  scans.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-slate-100 hover:bg-slate-50/60"
                      data-testid={`scan-row-${s.id}`}
                    >
                      <td className="px-6 py-3.5 text-sm text-slate-700 whitespace-nowrap font-mono">
                        {new Date(s.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium text-xs capitalize">
                          <DeviceIcon device={s.device} />
                          {s.device || "other"}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-sm text-slate-700">
                        {s.city || s.country
                          ? [s.city, s.region, s.country].filter(Boolean).join(", ")
                          : <span className="text-slate-400">Unknown</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <CampaignModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaign={campaign}
        onSaved={() => load()}
      />
    </div>
  );
}
